import type { MortalityLog } from '../types/production'

import { toLocalCalendarDayKey } from './dateNormalization'
import { mortalityInstantMs } from './countDayDeaths'
import { farmTimeZone } from './farmTime'

/**
 * Canonical daily mortality rollup for one (lot, calendar date) pair.
 *
 * Built by summing every `MortalityLog.mortalityCount` whose `recordedAt`
 * resolves to the same calendar day and whose `lot` matches.
 *
 * Per the v2.1 MORT-12 rule (locked 2026-06-18), when any `MortalityLog`
 * events exist for a given `(lot, date)`, this rollup IS the canonical daily
 * total. Consumers should prefer `totalCount` over any single event's
 * `mortalityCount` and over the `ProductionRecord.dailyMortality` field for
 * that day.
 */
export type DailyMortalityRollup = {
  /** Calendar date key in `YYYY-MM-DD` form (UTC interpretation). */
  date: string
  /** Trimmed lot label, or empty string when the source log omitted it. */
  lot: string
  /** Sum of `mortalityCount` across every contributing log. */
  totalCount: number
  /** IDs of every log that contributed, in input encounter order. */
  logIds: string[]
  /** Number of contributing logs. Equal to `logIds.length`. */
  eventCount: number
  /** Latest `recordedAt` value among contributing logs (raw ISO string). */
  latestRecordedAt: string
  /** Findings #203: when each death record of the day stands for (`mortalityInstantMs`) and its count. */
  timeline: Array<{ ms: number; count: number }>
}

const toCalendarDateKey = (raw: string | undefined | null, timeZone?: string): string => {
  // Group by the *local* calendar day so a ProductionRecord saved as
  // "2026-04-13T19:36" (datetime-local, no Z) and its MortalityLog
  // recorded as "2026-04-14T00:36:00.000Z" (UTC) land in the same bucket
  // when the user is in a behind-UTC time zone. See
  // [docs/release/v2.1/findings.md](../../docs/release/v2.1/findings.md)
  // row #7 for the v2.1 evidence.
  return toLocalCalendarDayKey(raw, timeZone)
}

const toCount = (raw: string | number | undefined | null): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0
  if (typeof raw !== 'string') return 0
  const trimmed = raw.trim()
  if (!trimmed) return 0
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : 0
}

const toLotKey = (raw: string | undefined | null): string => {
  if (typeof raw !== 'string') return ''
  return raw.trim()
}

/** Compose the `(date, lot)` lookup key used by the rollup map. */
export const buildDailyMortalityRollupKey = (date: string, lot: string): string => {
  return `${date}::${lot}`
}

/**
 * Group `MortalityLog` events by `(calendar date, lot)` and sum
 * `mortalityCount` per group.
 *
 * The returned map is keyed by `buildDailyMortalityRollupKey(date, lot)`.
 * Logs whose `recordedAt` cannot be parsed into a calendar date are skipped.
 *
 * The function is pure and side-effect free. It does not mutate the input.
 */
export const rollupMortalityLogsByLotDay = (
  logs: readonly MortalityLog[]
): Map<string, DailyMortalityRollup> => {
  const result = new Map<string, DailyMortalityRollup>()

  for (const log of logs) {
    // Crew Mode Field Bundle v0 §3 item 8: "No se pudo revisar" carries a
    // count of 0 only because the model needs one — it is not "zero deaths",
    // so it never makes a day read as recorded / 0 in the daily figure.
    if ((log.metadata as Record<string, unknown> | undefined)?.notChecked === true) continue

    // Findings #85: a record's day is its farm's day.
    const date = toCalendarDateKey(log.recordedAt, farmTimeZone(log.farmId))
    if (!date) continue

    const lot = toLotKey(log.lot)
    const key = buildDailyMortalityRollupKey(date, lot)
    const count = toCount(log.mortalityCount)
    const ms = mortalityInstantMs(log.recordedAt, farmTimeZone(log.farmId))
    const timelineEntries = ms !== null ? [{ ms, count }] : []

    const existing = result.get(key)
    if (existing) {
      existing.totalCount += count
      existing.timeline.push(...timelineEntries)
      existing.logIds.push(log.id)
      existing.eventCount = existing.logIds.length
      if (typeof log.recordedAt === 'string' && log.recordedAt > existing.latestRecordedAt) {
        existing.latestRecordedAt = log.recordedAt
      }
    } else {
      result.set(key, {
        date,
        lot,
        totalCount: count,
        logIds: [log.id],
        eventCount: 1,
        latestRecordedAt: typeof log.recordedAt === 'string' ? log.recordedAt : '',
        timeline: timelineEntries
      })
    }
  }

  return result
}

/**
 * Convenience lookup: get the rollup that matches a `(rawDate, lot)` pair,
 * normalizing the date the same way as `rollupMortalityLogsByLotDay`.
 */
export const findDailyMortalityRollup = (
  rollup: ReadonlyMap<string, DailyMortalityRollup>,
  rawDate: string,
  lot: string | undefined | null
): DailyMortalityRollup | undefined => {
  const date = toCalendarDateKey(rawDate)
  if (!date) return undefined
  return rollup.get(buildDailyMortalityRollupKey(date, toLotKey(lot)))
}
